import Card from "../Card/Card";
import {
  ICandidatesListProps,
  SortDirection,
  SortDirectionField,
  SortField,
} from "../types";
import style from "./CandidatesList.module.scss";
import {
  NoData,
  ContextMenu,
  PdfViewer,
  SkeletonTable,
  RightSidebar,
} from "@/components";
import { ROUTE_EDIT_CANDIDATE } from "@/constants/routes";
import {
  candidateCustomApi,
  useCandidateDeleteMutation,
} from "@/core/api/candidate/candidate";
import { store } from "@/core/store";
import { useUserStore } from "@/core/store/UserDataStore";
import { setState } from "@/core/store/reducers/ContextMenuSlice";
import useErrorNotification from "@/hooks/useErrorNotification";
import { useRightSidebarState } from "@/hooks/useRightSidebarState";
import { DocIcon } from "@/services/docicon.service";
import {
  Button,
  Indicator,
  Popup,
  Tooltip,
  useSnackbars,
} from "@aurora/components";
import {
  GlyphBin,
  GlyphEdit,
  GlyphForbidden,
  GlyphMore,
  GlyphSurvey,
  SmallFall,
  SmallGrow,
} from "@aurora/icons";
import cx from "classnames";
import LinkWithVacancy from "components/BaseComponents/Popups/LinkWithVacancy/Modal";
import { DICTIONARY } from "constants/dictionary";
import { useHasAnyNewMessagesMutation } from "core/api/chat/chat";
import React, { FC, useEffect, useState } from "react";
import ReactDOM from "react-dom";
import { useNavigate, useSearchParams } from "react-router-dom";
import { snackbarSuccess } from "services/snackbar.service";

const initSortDirection: SortDirectionField = {
  fullName: undefined,
  resumeUploadDate: undefined,
};

const CandidatesList: FC<ICandidatesListProps> = ({
  candidatesData,
  isCandidatesLoading,
  showStatuses = true,
  updateCandidateData,
}: any) => {
  const { snackbarInfo } = useSnackbars();
  const navigate = useNavigate();
  const errorNotification = useErrorNotification();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isManagerOrWatcher } = useUserStore();

  const [isRightSidebarOpen, setIsRightSidebarOpen] = useRightSidebarState();
  const [candidateDeleteId, setCandidateDeleteId] = useState<string>("");
  const [candidateSelected, setCandidateSelected] = useState<any>();
  const [hasAnyMessages, setHasAnyMessages] = useState<any>({});
  const [showConfirm, changeShowConfirm] = useState(false);
  const [isShowVacancyPopup, setShowVacancyPopup] = useState(false);
  const [bindVacancyCallback, setCallbackOfBindVacancy]: any = useState();
  const [sortDirection, setSortDirection] =
    useState<SortDirectionField>(initSortDirection);
  const [contextMenuList, setContextMenuList]: any[] = useState([]);
  const [showDocumentView, setShowDocumentView] = useState<boolean>(false);
  const [documentLoaded, setDocumentLoaded] = useState<string | null>(null);
  const [currentCandidateId, setCurrentCandidateId] = useState<string>("");

  const [
    updateCardCallbackAfterChangeStatus,
    setNewCallbackForUpdateCard,
  ]: any = useState(null);

  const [deleteCandidate] = useCandidateDeleteMutation();
  const [hasAnyNewMessages] = useHasAnyNewMessagesMutation();

  function openFileInNewTab(candidateId: string) {
    const link = document.createElement("a");
    candidateCustomApi.downloadResume(candidateId).then(file => {
      if (!file.data) return;
      link.href = URL.createObjectURL(file.data);
      link.download = file.name || `resume_${candidateId}_download`; // имя файла для скачивания (можно задать любое)
      link.dispatchEvent(new MouseEvent("click"));
    });
  }

  function viewFile(candidateId: string) {
    setCurrentCandidateId(candidateId);
    candidateCustomApi.downloadResume(candidateId).then(file => {
      if (!file.data) return;
      setDocumentLoaded(URL.createObjectURL(file.data));
    });
  }

  async function loadStatusOfMessages() {
    const messages: any = await hasAnyNewMessages();
    if (messages.error) return;
    const mess = messages?.data.reduce((acc: any, item: string) => {
      acc[item] = true;
      return acc;
    }, {});
    setHasAnyMessages(mess);
  }

  function getActiveSelectedItemById(id: string) {
    const candidateItem = candidatesData?.items.filter((item: any) => {
      return item.id === id;
    })[0];
    if (!candidateItem) return { id };

    return {
      ...candidateItem,
      hasNewMessages: hasAnyMessages[candidateItem.id],
    };
  }

  useEffect(() => {
    if (searchParams.get("candidateId")) {
      setCandidateSelected({ id: searchParams.get("candidateId") });
    }
    setIsRightSidebarOpen(!!searchParams.get("candidateId"));

    loadStatusOfMessages();
    const interval = setInterval(loadStatusOfMessages, 5000);

    return () => {
      clearInterval(interval);
    };
  }, []);

  useEffect(() => {
    if (!isCandidatesLoading) {
      updateCandidateData(sortDirection);
    }
  }, [sortDirection]);

  const header = [
    { element: "", item: "marker" },
    {
      element: (
        <div>
          ФИО <br /> кандидата
        </div>
      ),
      item: "fullName",
    },
    {
      element: (
        <div>
          Контактные
          <br />
          данные
        </div>
      ),
      item: "contacts",
    },
    { element: <div>Дата резюме</div>, item: "resumeUploadDate" },
    { element: <div>Файл</div>, item: "file" },
    { element: "", item: "actionBtn" },
  ];

  const tableHead = header.map((item: any) => {
    const sortIcon = (
      <div
        className={cx(
          style.CandidatesTable__icon,
          style.CandidatesTable__icon__align_left,
        )}
      >
        {sortDirection[item.item as SortField] === "asc" && (
          <SmallFall data-testid="sort-down" />
        )}
        {sortDirection[item.item as SortField] === "desc" && (
          <SmallGrow data-testid="sort-up" />
        )}
      </div>
    );

    function onSort(item: SortField) {
      setSortDirection(prevState => {
        const currentDirection = prevState[item];
        let newDirection: SortDirection | undefined;
        if (currentDirection === undefined) {
          newDirection = "asc";
        } else if (currentDirection === "asc") {
          newDirection = "desc";
        } else {
          newDirection = undefined;
        }
        return { ...initSortDirection, [item]: newDirection };
      });
    }

    return (
      <th
        key={item.item}
        className={cx(
          item.item in sortDirection && style.CandidatesTable__point,
        )}
        onClick={() => {
          if (item.item in sortDirection) onSort(item.item);
        }}
      >
        {item.item in sortDirection && sortIcon}
        {item.element}
      </th>
    );
  });

  const getList = (item: any): any[] => {
    return [
      {
        icon: <GlyphEdit className="text-accent" />,
        name: "Редактировать",
        onClick: () => {
          navigate(`${ROUTE_EDIT_CANDIDATE}/${item.id}`);
        },
      },
      {
        icon: <GlyphSurvey className="text-accent" />,
        name: "Связи с вакансиями",
        onClick: () => {
          setCandidateSelected(item);
          setShowVacancyPopup(true);
        },
      },
      {
        icon: <GlyphBin className="text-accent" />,
        name: "Удалить",
        onClick: () => {
          setCandidateDeleteId(item.id);
          changeShowConfirm(true);
        },
      },
    ];
  };
  return (
    <div className={style.CandidatesListContainer}>
      {candidatesData?.total <= 0 ? (
        <NoData />
      ) : (
        <table className={style.CandidatesTable}>
          <colgroup>
            <col width={"32px"} />
            <col width={"60%"} />
            <col width={"40%"} />
            <col width={"130px"} />
            <col width={"70px"} />
            <col width={"90px"} />
          </colgroup>
          <thead>
            <tr>{tableHead.map((element: any) => element)}</tr>
          </thead>
          <tbody>
            {isCandidatesLoading ? (
              <SkeletonTable rows={10} />
            ) : (
              candidatesData?.items.map((item: any) => {
                return (
                  <tr
                    key={item.id}
                    className="border-top"
                    onClick={() => {
                      setIsRightSidebarOpen(true);
                      setCandidateSelected(item);
                    }}
                  >
                    <td>
                      {item.inBlackList && (
                        <Tooltip
                          content={"Кандидат в черном списке"}
                          placement="topLeft"
                          triggerOn="hover"
                        >
                          <GlyphForbidden className="text-destructive" />
                        </Tooltip>
                      )}
                    </td>
                    <td title={item.fullName}>
                      {hasAnyMessages[item.id] ? (
                        <span
                          className={cx("mr-4", style.indicatorNewMessages)}
                        >
                          <Indicator shape="point" variant="blueActive" />
                        </span>
                      ) : null}
                      {item.fullName}
                    </td>
                    <td>
                      <div className="pb-2">{item.phone || null}</div>
                      <div className="rem-09">{item.email || null}</div>
                    </td>
                    <td>
                      {item.resumeUploaded
                        ? new Date(item.resumeUploadDate).toLocaleDateString()
                        : "-"}
                    </td>
                    <td align={"center"}>
                      <>
                        {item.resumeUploaded ? (
                          <div className={style.IconViewers}>
                            <a
                              className={"link"}
                              onClick={(e?: React.MouseEvent) => {
                                e?.stopPropagation();
                                if (
                                  item.resumeFileName
                                    .toLowerCase()
                                    .endsWith(".pdf")
                                ) {
                                  viewFile(item.id);
                                } else {
                                  openFileInNewTab(item.id);
                                }
                              }}
                            >
                              <DocIcon fileName={item.resumeFileName} />
                            </a>
                          </div>
                        ) : (
                          <span>&nbsp;-</span>
                        )}
                      </>
                    </td>
                    <td
                      className={cx(style.Icons, "text-right", "p-4")}
                      style={{ position: "relative" }}
                    >
                      {!isManagerOrWatcher && (
                        <Button
                          className={cx(
                            style.AdditionallyMenuButton,
                            style.CreateButton,
                            "bg-content-2",
                            "text-black",
                            "p-6",
                          )}
                          iconLeft={<GlyphMore />}
                          onClick={(event: any) => {
                            event.preventDefault();
                            event.stopPropagation();
                            const list = getList(item);
                            setContextMenuList(list);
                            const rect = event.target
                              .closest("td")
                              .getBoundingClientRect();

                            store.dispatch(
                              setState({
                                visible: true,
                                x: rect.width + rect.x,
                                y: rect.y - rect.height,
                              }),
                            );
                          }}
                          shape={"default"}
                          variant={"primary"}
                        >
                          {""}
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      )}

      {isShowVacancyPopup && (
        <LinkWithVacancy
          candidate={getActiveSelectedItemById(candidateSelected.id)}
          clickOnClose={() => {
            setShowVacancyPopup(false);
            updateCandidateData();
            if (bindVacancyCallback) {
              bindVacancyCallback();
              setCallbackOfBindVacancy(null);
            }
          }}
          type="candidate"
        />
      )}

      <ContextMenu list={contextMenuList} />

      {!showStatuses &&
        ReactDOM.createPortal(
          <>
            <RightSidebar
              onClose={() => {
                setSearchParams({});
                setIsRightSidebarOpen(false);
              }}
              show={isRightSidebarOpen}
            >
              {isRightSidebarOpen && (
                <Card
                  candidate={getActiveSelectedItemById(candidateSelected.id)}
                  onCloseBindCandidateModal={(callback: any) => {
                    setCallbackOfBindVacancy(callback);
                  }}
                  openBindCandidateModal={setShowVacancyPopup}
                  openUpdateStatus={(callbackStatusUpdate: any) => {
                    setNewCallbackForUpdateCard(callbackStatusUpdate);
                  }}
                />
              )}
            </RightSidebar>
          </>,
          document.body,
        )}
      {!showDocumentView &&
        ReactDOM.createPortal(
          <>
            <RightSidebar
              onClose={() => {
                setDocumentLoaded(null);
                setShowDocumentView(false);
              }}
              show={!!documentLoaded}
            >
              {!!documentLoaded && (
                <PdfViewer
                  candidateId={currentCandidateId}
                  url={documentLoaded}
                />
              )}
            </RightSidebar>
          </>,
          document.body,
        )}

      {showConfirm && (
        <Popup
          classNameContent={cx(style.deleteCandidateContent)}
          classNameWrapper={cx(style.deleteCandidateWrapper)}
          onClickCloseCross={() => {
            changeShowConfirm(false);
          }}
          onClickOutside={() => {
            changeShowConfirm(false);
          }}
        >
          <div className="p-12">
            <h1 className="text-accent my-0">Удалить карточку кандидата?</h1>
            <p className="my-12">
              Вы уверены, что хотите удалить карточку кандидата? После этого ее
              нельзя будет восстановить.
            </p>
            <div className={cx(style.deleteCandidateButtons)}>
              <Button
                className="mr-4"
                onClick={() => {
                  changeShowConfirm(false);
                }}
                variant="secondary"
              >
                Отмена
              </Button>
              <Button
                onClick={async () => {
                  const resultOfDelete: any = await deleteCandidate(
                    candidateDeleteId,
                  );

                  if (resultOfDelete.error) {
                    errorNotification(resultOfDelete.error);
                  } else {
                    const candidate = candidatesData?.items.filter(
                      (candidate: any) => {
                        return candidate.id === candidateDeleteId;
                      },
                    )[0];

                    const success: any = snackbarSuccess(
                      DICTIONARY.DELETE_CANDIDATE(candidate.fullName),
                    );
                    snackbarInfo?.show(success);
                  }

                  updateCandidateData();
                  changeShowConfirm(false);
                }}
                variant="sell"
              >
                Удалить
              </Button>
            </div>
          </div>
        </Popup>
      )}
    </div>
  );
};

export default CandidatesList;
